/** @module application */

import $ from 'jquery';
import { liveEvents } from '../../../../assets/js/application/index.js';

const filterURLParamName = 'cbf'; // = content-band filter

/**
 * Should not be referenced directly.
 *
 * @param {NodeList} filters
 *
 * @private
 * @see {@link filters}
 */
export async function initFilters(filters) {
    const events = await liveEvents(),
        url = new URL(location.href);

    let initialFilterSettings;

    try {
        initialFilterSettings = Object.fromEntries(
            new URLSearchParams(atob(url.searchParams.get(filterURLParamName) || '')),
        );
    } catch (e) {
        initialFilterSettings = {};
    }

    filters.forEach((filter) => {
        const inputs = filter.querySelectorAll('input[type="checkbox"]'),
            targets = document.querySelectorAll(
                filter.dataset.filter
                    .split(',')
                    .map((f) => `[data-filter-target='${f}']`)
                    .join(','),
            ),
            itemSelector = filter.dataset.item || '[data-filter-item]',
            logic = filter.dataset.logic || 'OR',
            resetButton = filter.querySelector('[data-filter-reset]'),
            filterChangedEvent = new CustomEvent('filter.changed', { bubbles: true, cancelable: false }),
            itemsFilteredEvent = new CustomEvent('filter.filtered', { bubbles: true, cancelable: false }),
            cachedItems = new WeakMap(),
            preFilteredItems = new WeakMap(),
            initialSettings = (initialFilterSettings[filter.id] || '').split(logic === 'OR' ? ',' : '+');

        function onInputChange() {
            const checkedInputs = filter.querySelectorAll('.filter__term input:checked'),
                notCheckedInputs = filter.querySelectorAll('.filter__term input:not(:checked)');

            // set active styles on input parent because of no possibility via CSS because of DOM structure
            checkedInputs.forEach((input) => {
                input.closest('.filter__term').classList.add('filter__term--active');
            });

            notCheckedInputs.forEach((input) => {
                input.closest('.filter__term').classList.remove('filter__term--active');
            });
            // show/hide the corresponding items
            // NOTE: requestAnimationFrame is important to ensure that hierarchical inputs are done with their updates
            requestAnimationFrame(() => {
                const checkedTerms = Array.prototype.map.call(
                        filter.querySelectorAll('input:checked') || [],
                        ({ value }) => value,
                    ),
                    url = new URL(location.href);

                let cbf;

                /* eslint-disable one-var */
                // necessary for room-finder filter
                const filterTerms = JSON.parse(filter.dataset.filterTerms);

                let filteredItemSlugs = [];
                /* eslint-enable */

                try {
                    const filterParams = new URLSearchParams(
                        atob(decodeURIComponent(url.searchParams.get(filterURLParamName) || '')),
                    );

                    cbf = new URLSearchParams({
                        ...Object.fromEntries(filterParams),
                        [filter.id]: checkedTerms.join(logic === 'OR' ? ',' : '+'),
                    });
                } catch (e) {
                    cbf = new URLSearchParams({
                        [filter.id]: checkedTerms.join(logic === 'OR' ? ',' : '+'),
                    });
                }

                if (checkedTerms.length === 0) {
                    cbf.delete(filter.id);
                }

                if (cbf.toString()) {
                    url.searchParams.set(filterURLParamName, btoa(cbf));
                } else {
                    url.searchParams.delete(filterURLParamName);
                }

                history.replaceState(undefined, document.title, url);

                targets.forEach((target) => {
                    let items = [];

                    if (preFilteredItems.has(target)) {
                        items = preFilteredItems.get(target);
                    }

                    if (items.length === 0) {
                        if (cachedItems.has(target) === false) {
                            cachedItems.set(target, target.querySelectorAll(`${itemSelector}:not(.filter)`));
                        }

                        items = cachedItems.get(target);
                    }

                    items.forEach((item) => {
                        let show = true,
                            terms = JSON.parse(item.dataset.filterItem || '[]');

                        if (checkedTerms.length) {
                            if (logic === 'OR') {
                                show = checkedTerms.some((term) => terms.includes(term));
                            } else if (logic === 'AND') {
                                show = checkedTerms.every((term) => terms.includes(term));
                            }
                        }

                        item.hidden = !show;

                        if (item.hidden === false) {
                            let filterItemSlug = JSON.parse(item.dataset.filterItem);

                            filteredItemSlugs = filteredItemSlugs.concat(filterItemSlug);
                        }
                    });

                    // room-finder
                    if (filterTerms) {
                        // remove duplicate slugs
                        let uniqueFilteredItemSlugs = [...new Set(filteredItemSlugs)];

                        const termsToDisable = filterTerms.filter((item) => !uniqueFilteredItemSlugs.includes(item)),
                            termsToActivate = filterTerms.filter((item) => !termsToDisable.includes(item));

                        termsToDisable.forEach((item) => {
                            let input = document.querySelector(`input[name="filter"][value="${item}"]`);

                            if (input) {
                                input.closest('.filter__term').classList.add('filter__term--disabled');
                                input.disabled = true;
                            }
                        });

                        termsToActivate.forEach((item) => {
                            let input = document.querySelector(`input[name="filter"][value="${item}"]`);

                            if (input) {
                                input.closest('.filter__term').classList.remove('filter__term--disabled');
                                input.disabled = false;
                            }
                        });
                    }

                    // inform dependent components that the filtered items changed
                    target.dispatchEvent(itemsFilteredEvent);
                });
            });
        }

        inputs.forEach((input) => {
            input.checked =
                Boolean(initialSettings.length) && Boolean(initialSettings.find((setting) => setting === input.value));

            input.addEventListener('change', onInputChange);
        });

        // set initial settings active
        if (inputs.length) {
            onInputChange();

            if (resetButton) {
                resetButton.addEventListener(
                    'click',
                    () => {
                        inputs.forEach((input) => {
                            input.checked = false;
                        });

                        onInputChange();
                    },
                    { passive: true },
                );
            }
        }

        // inform dependent components that the filter component changed the layout.
        // unfortunately, it is not possible to listen for jQuery events in vanilla JS
        $(filter).on('down.zf.accordionMenu', () => {
            targets.forEach((target) => {
                target.dispatchEvent(filterChangedEvent);
            });
        });

        events.addEventListener(document, 'filter.searched', (event) => {
            if (event.target.dataset.search === filter.dataset.filter) {
                preFilteredItems.set(event.detail.target, event.detail.results);

                inputs.forEach((input) => {
                    input.checked = false;
                });
            }
        });
    });
}
